import { Component, ChangeDetectorRef, effect } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PositionData } from '@interfaces/open-positions.model';
import { HiringManagerOpenpositionsService } from './hiring-manager-openpositions.service';
import { ColumnDefParams, TooltipPlacement } from '@emerald/nxcore';
import { columnDefs } from './hiring-manager-openpositions.config';
import { paginationParams, tabsData } from '@shared/config/table.config';
import { IEmeraldTabData } from '@emerald/nxcore';
import { HiringManagerCandidatesService } from '../hiring-manager-candidates/hiring-manager-candidates.service';
import { GlobalErrorService } from '@services/global-error.service';
import { SearchService } from '@services/search.service';
import { PersonaType, TabLabels, Priority, RoutesEnum } from '@constants/global.enum';
import { mapHiringRequest } from './hiring-manager-openpositions.helper';
import { dateFormat } from '@constants/dateFormat';
import { DataService } from '@services/data.service';
import { interval, takeWhile } from 'rxjs';
import { DEFAULT_LEGEND_ITEMS } from '@constants/constants';
import { truncateText as truncateTextUtil } from '@utils/utils';

@Component({
  selector: 'app-hiring-manager-openpositions',
  templateUrl: './hiring-manager-openpositions.component.html',
  styleUrl: './hiring-manager-openpositions.component.scss',
})
export class HiringManagerOpenpositionsComponent {
  routesEnum = RoutesEnum;
  columnDefs: ColumnDefParams<any>[] = columnDefs;
  paginationParams = paginationParams;
  tabsData = tabsData;
  openPositions: PositionData[] = [];
  displayData: PositionData[] = [];
  baseFilteredData: PositionData[] = []; // Store data from filter component
  positionCount = 0;
  selectedTab!: IEmeraldTabData;
  errorMessage: string | null = null;
  candidatesCount = 0;
  imageSrc: string = 'assets/img/no-data-found.png';
  fieldsVisibility: string = PersonaType.HireMgrOpenPos;
  filterText: string = '';
  tooltipPosition: TooltipPlacement = 'top-start';
  offset: [number, number] = [0, 8];
  tabLabel = TabLabels;
  isDataLoaded: boolean = false;
  priority = Priority;
  hiringRequestId: number[] = [0];
  employeeLevel: number = 0;
  dateFormat = dateFormat;
  tooltipDelayShow = 500;
  tooltipDelayHide = 200;
  columnTextDisplayLimit = 20;
  primarySkillsDisplayLimit = 8;
  // Only the Critical flag needs a legend; "Standard" adds no information.
  legendItems = DEFAULT_LEGEND_ITEMS.filter((i) => i.label?.toLowerCase() === 'critical');
  candidatesData: any[] = [];
  public truncateText = truncateTextUtil;
  showJobDetails = false;
  selectedJob: PositionData | null = null;
  private tabInitCount = 0;
  private readonly TAB_INIT_FIRES = 2;
  private candidatesCountFetched = false;
  // When the initial fetch is skipped because the user profile hasn't hydrated
  // yet, the poll re-triggers it. Bound the retries so a genuinely profile-less
  // session eventually renders the empty state instead of polling forever.
  private openPositionsRetries = 0;
  private readonly MAX_OPEN_POSITIONS_RETRIES = 20;

  // ---- op-table client-side sort / pagination / tooltip state ----
  sortColumn = '';
  sortDir: 'asc' | 'desc' = 'asc';
  currentPage = 1;
  localPageSize = paginationParams.pageSize;
  hoverTip = { show: false, text: '', x: 0, y: 0 };

  constructor(
    private router: Router,
    private openPositionsService: HiringManagerOpenpositionsService,
    private candidatesService: HiringManagerCandidatesService,
    private errorHandler: GlobalErrorService,
    private searchService: SearchService,
    private dataService: DataService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
  ) {
    effect(() => {
      const signalData = this.candidatesService.candidatesSignal();
      const totalRecords = signalData?.data?.totalRecords;
      if (totalRecords !== undefined) {
        this.candidatesCount = totalRecords;
        this.tabsData[1].badgeContent = totalRecords.toString();
        this.cdr.markForCheck();
      }
    });
  }

  ngOnInit() {
    const userProfile = this.dataService?.userProfile();
    this.employeeLevel = userProfile?.employeeLevel;
    // Always fetch fresh data on entering the screen — the service's signal caches the
    // previous visit's response, so clear it and force a new API call.
    this.openPositionsService.openPositionsSignal.set(null);
    this.openPositionsRetries = 0;
    this.openPositionsService.refreshOpenPositions();
    this.getInterviewCandidate();
    this.loadOpenPositions();
  }

  onTabChange(tab: IEmeraldTabData) {
    this.selectedTab = tab;
    if (this.tabInitCount < this.TAB_INIT_FIRES) {
      this.tabInitCount++;
      return;
    }

    if (tab?.label === this.tabLabel.Candidates) {
      this.candidatesService.candidatesSignal.set(null);
    }

    if (tab?.label === this.tabLabel.OpenPositions) {
      this.openPositionsService.openPositionsSignal.set(null);
      this.openPositionsService.refreshOpenPositions();
    }
  }

  getInterviewCandidate(){
    this.route.queryParams.subscribe(params => {
      const isCandidatesTab = params['tab'] === 'candidates';
      this.tabsData.forEach(tab => {
      tab.activeTab = tab.isTabActive = isCandidatesTab && tab.value === 'candidates';
      });
    });
  }
  activateTab(tabValue: string) {
    this.tabsData.forEach(tab => {
      tab.activeTab = (tab.value === tabValue);
      tab.isTabActive = (tab.value === tabValue);
    });
  }

  private loadOpenPositions(): void {
    // Loads open positions by periodically fetching data until the data is fully loaded.
    interval(1000)
      .pipe(takeWhile(() => !this.isDataLoaded))
      .subscribe({
        next: () => {
          this.fetchOpenPositions();
        },
        error: (error) => {
          this.isDataLoaded = true;
          this.errorHandler.logComponentError(error);
        },
      });
  }

  private fetchOpenPositions(): void {
    const signalData = this.openPositionsService.openPositionsSignal();
    if (signalData && signalData?.data) {
      this.openPositions = signalData?.data
        ?.map(mapHiringRequest);

      this.openPositions = this.openPositions || [];
      this.isDataLoaded = true;
      if (this.baseFilteredData.length === 0) {
        if (this.activeFilterCount) {
          // apply the default / currently active filters (e.g. Status: Open) to the fresh load
          this.applyLocalFilters();
        } else {
          this.displayData = [...this.openPositions];
        }
      }

      this.hiringRequestId = this.openPositions?.map(
        (candidate) => candidate?.hiringRequestId,
      );

      this.getCandidateCount();
      this.getContPositions();
    } else if (this.openPositionsRetries++ < this.MAX_OPEN_POSITIONS_RETRIES) {
      // Signal still empty — the initial fetch was likely skipped because the
      // user profile hadn't hydrated yet. Re-trigger the service fetch (a no-op
      // while one is in flight) so it fires as soon as the profile is ready,
      // instead of leaving the screen stuck on zero positions.
      this.openPositionsService.refreshOpenPositions();
    } else {
      // Safety cap reached — stop polling and let the empty state render.
      this.isDataLoaded = true;
    }
  }

  getCandidateCount() {
    if (this.candidatesCountFetched) return;
    this.candidatesCountFetched = true;
    this.candidatesService.getCandidates(this.hiringRequestId, 1, 10);
  }

  transformSharedByVendor(vendors: string[]): string {
    if (!vendors || vendors.length === 0) {
      return '';
    }
    return vendors.join(', ');
  }

  searchItem(event: KeyboardEvent) {
    this.filterText = this.searchService.getFilteredValue(event);
    
    const searchBase = this.baseFilteredData.length > 0 ? this.baseFilteredData : this.openPositions; 
    this.displayData =
      searchBase && searchBase.length
        ? this.searchService.getSearchedData(
            searchBase,
            this.filterText?.toLowerCase(),
          )
        : [];

    this.currentPage = 1;
    this.getContPositions();
    this.cdr.detectChanges();
  }

  clearInput() {
    this.displayData = this.baseFilteredData.length > 0 ? [...this.baseFilteredData] : [...this.openPositions];
    this.filterText = '';
    this.currentPage = 1;
    this.getContPositions();
    this.cdr.detectChanges();
  }

  getContPositions() {
    this.positionCount = (this.displayData ?? []).reduce(
      (total, position) => total + (position?.numberOfPositions ?? 0),
      0,
    );
    this.tabsData[0].badgeContent = this.positionCount.toString();
  }
  raiseHirinRequest() {
    this.router.navigate(['hiring-manager/hiringrequest']);
  }
  handleDataFromChild(data: PositionData[]) {
    if (!data) {
      return;
    }
    
    this.baseFilteredData = [...data];    
    if (this.filterText && this.filterText.trim()) {
      this.displayData = this.searchService.getSearchedData(
        this.baseFilteredData,
        this.filterText.toLowerCase(),
      );
    } else {
      this.displayData = [...this.baseFilteredData];
    }

    this.currentPage = 1;
    this.getContPositions();
    this.cdr.detectChanges();
  }

  // ---- vendor-style inline filter panel (client-side over openPositions; feeds the
  // same handleDataFromChild pipeline the previous shared filter used) ----
  showFilterPanel = false;
  // Open positions default to the "Open" status (like the vendor flow); the chip
  // makes the default visible and the user can untick it in the Filters panel.
  opFilterDraft = { statuses: ['Open'] as string[], lobName: '', projectName: '', vendorName: '', priority: '' };
  opActiveFilters = { statuses: ['Open'] as string[], lobName: '', projectName: '', vendorName: '', priority: '' };

  toggleDraftStatus(s: string): void {
    const i = this.opFilterDraft.statuses.indexOf(s);
    if (i >= 0) {
      this.opFilterDraft.statuses.splice(i, 1);
    } else {
      this.opFilterDraft.statuses.push(s);
    }
  }

  get facetStatus(): string[] { return this.opFacetOf('status'); }
  get facetLob(): string[] { return this.opFacetOf('lobName'); }
  get facetProject(): string[] { return this.opFacetOf('projectName'); }
  get facetVendor(): string[] {
    // one entry per unique vendor name (case-insensitive), never comma-joined lists
    const seen = new Map<string, string>();
    (this.openPositions || []).forEach((p: any) => {
      this.vendorNamesOf(p).forEach((name) => {
        const key = name.toLowerCase();
        if (!seen.has(key)) {
          seen.set(key, name);
        }
      });
    });
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
  }
  // A row's vendors as individual trimmed names — splits commas even inside array elements.
  private vendorNamesOf(row: any): string[] {
    const v = row?.vendorName;
    const raw: string[] = Array.isArray(v) ? v : typeof v === 'string' ? [v] : [];
    const parts: string[] = [];
    raw.forEach((s) => parts.push(...(s ?? '').toString().split(',')));
    return parts.map((s) => s.trim()).filter(Boolean);
  }
  private opFacetOf(key: string): string[] {
    const vals = (this.openPositions || [])
      .map((p: any) => (p?.[key] ?? '').toString().trim())
      .filter((v: string) => v && v.toLowerCase() !== 'null' && v.toLowerCase() !== 'undefined');
    return Array.from(new Set(vals)).sort((a, b) => a.localeCompare(b));
  }
  get activeFilterCount(): number {
    const f = this.opActiveFilters;
    let n = f.statuses.length ? 1 : 0;
    if (f.lobName) n++;
    if (f.projectName) n++;
    if (f.vendorName) n++;
    if (f.priority) n++;
    return n;
  }

  toggleFilterPanel(): void {
    this.showFilterPanel = !this.showFilterPanel;
    if (this.showFilterPanel) {
      this.opFilterDraft = { ...this.opActiveFilters, statuses: [...this.opActiveFilters.statuses] };
    }
  }

  applyFilterPanel(): void {
    this.opActiveFilters = { ...this.opFilterDraft, statuses: [...this.opFilterDraft.statuses] };
    this.showFilterPanel = false;
    this.applyLocalFilters();
  }

  resetFilterPanel(): void {
    this.opFilterDraft = { statuses: [], lobName: '', projectName: '', vendorName: '', priority: '' };
    this.opActiveFilters = { statuses: [], lobName: '', projectName: '', vendorName: '', priority: '' };
    this.showFilterPanel = false;
    this.applyLocalFilters();
  }

  private applyLocalFilters(): void {
    const f = this.opActiveFilters;
    let rows: any[] = [...(this.openPositions || [])];
    if (f.statuses.length) rows = rows.filter((r) => f.statuses.includes(r?.status));
    if (f.lobName) rows = rows.filter((r) => r?.lobName === f.lobName);
    if (f.projectName) rows = rows.filter((r) => r?.projectName === f.projectName);
    if (f.vendorName) {
      rows = rows.filter((r) => this.vendorNamesOf(r).includes(f.vendorName));
    }
    if (f.priority) {
      const wantCritical = f.priority === 'Critical';
      rows = rows.filter((r) => this.isRowPriority(r) === wantCritical);
    }
    this.handleDataFromChild(rows as PositionData[]);
  }

  editRow(data: PositionData) {
    this.router.navigate(['hiring-manager/hiringrequest'], {
      state: { formState: 'edit', openPositionData: data },
    });
  }

  showJobDtls(data: PositionData) {
    this.selectedJob = data;
    this.showJobDetails = true;
  }

  /** Back from the Job Details overlay: the position's status may have been
   *  changed there, and this component stays alive underneath the modal (no
   *  ngOnInit re-run) — force a fresh fetch so the list reflects the update. */
  onJobDetailsClosed(): void {
    this.showJobDetails = false;
    this.openPositionsService.openPositionsSignal.set(null);
    this.openPositionsRetries = 0;
    this.openPositionsService.refreshOpenPositions();
    this.baseFilteredData = [];
    this.isDataLoaded = false;
    this.loadOpenPositions();
  }

  navigateToVendorManagement(): void {
    this.router.navigate([RoutesEnum.VendorManagement]);
  }
  handleCandidatesDataChange(candidatesData: any[]): void {
    this.candidatesData = candidatesData;
    this.cdr.detectChanges();
  }

  // ---- op-table: priority flag + status pill + skill join ----
  isRowPriority(row: PositionData): boolean {
    return row?.isPriority?.toString() === 'true';
  }

  statusClass(status?: string): string {
    const v = (status || '').toLowerCase();
    if (v === 'open') return 'open';
    if (v === 'onhold' || v === 'on hold') return 'hold';
    return 'closed';
  }

  joinSkills(skills?: string[] | null): string {
    return Array.isArray(skills) ? skills.join(', ') : skills || '';
  }

  // ---- op-table: sort ----
  sortBy(field: string): void {
    if (this.sortColumn === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = field;
      this.sortDir = 'asc';
    }
    this.currentPage = 1;
  }

  sortIndicator(field: string): string {
    return this.sortColumn === field ? this.sortDir : '';
  }

  private sortValue(row: any, col: string): string | number {
    const v = row?.[col];
    if (Array.isArray(v)) return v.join(', ');
    return v;
  }

  get sortedRows(): any[] {
    const rows = [...(this.displayData || [])] as any[];
    if (!this.sortColumn) return rows;
    const dir = this.sortDir === 'asc' ? 1 : -1;
    const col = this.sortColumn;
    return rows.sort((a: any, b: any) => {
      const av = this.sortValue(a, col);
      const bv = this.sortValue(b, col);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (col === 'desiredStartDate') {
        return (new Date(av).getTime() - new Date(bv).getTime()) * dir;
      }
      const an = parseFloat(av as string);
      const bn = parseFloat(bv as string);
      if (
        (col === 'numberOfPositions' ||
          col === 'noOfCandidates' ||
          col === 'hiringRequestId') &&
        !isNaN(an) &&
        !isNaN(bn)
      ) {
        return (an - bn) * dir;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
  }

  // ---- op-table: client-side pagination ----
  get pagedRows(): any[] {
    const start = (this.currentPage - 1) * this.localPageSize;
    return this.sortedRows.slice(start, start + this.localPageSize);
  }

  get totalRecords(): number {
    return this.displayData?.length || 0;
  }

  get currentPageValue(): number {
    return this.currentPage;
  }

  get pageSizeValue(): number {
    return this.localPageSize;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil((this.totalRecords || 0) / this.pageSizeValue));
  }

  get rangeStart(): number {
    return this.totalRecords === 0 ? 0 : (this.currentPageValue - 1) * this.pageSizeValue + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPageValue * this.pageSizeValue, this.totalRecords);
  }

  get pageWindow(): number[] {
    const total = this.totalPages;
    const cur = this.currentPageValue;
    const out: number[] = [];
    if (total <= 7) {
      for (let i = 1; i <= total; i++) out.push(i);
      return out;
    }
    out.push(1);
    const start = Math.max(2, cur - 1);
    const end = Math.min(total - 1, cur + 1);
    if (start > 2) out.push(-1);
    for (let i = start; i <= end; i++) out.push(i);
    if (end < total - 1) out.push(-1);
    out.push(total);
    return out;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.currentPage = page;
  }

  prevPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  nextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  onPageSizeChange(event: Event): void {
    this.localPageSize = +(event.target as HTMLSelectElement).value;
    this.currentPage = 1;
  }

  trackByIndex(index: number): number {
    return index;
  }

  // ---- op-table: JS hover tooltip (only when a cell's text is truncated) ----
  showTip(event: MouseEvent, text?: string | null): void {
    if (!text) return;
    const cell = event.currentTarget as HTMLElement;
    const inner = (cell.querySelector('.op-tip-txt') as HTMLElement) || cell;
    if (inner.scrollWidth <= inner.clientWidth + 1) return;
    const rect = cell.getBoundingClientRect();
    this.hoverTip = { show: true, text, x: rect.left, y: rect.bottom + 4 };
  }

  hideTip(): void {
    this.hoverTip.show = false;
  }
}